import { CalendlyLogResponse } from '../useMemberCalendlyCall';

interface CalendlyCallData {
    isScheduled: boolean;
    slug: string | undefined;
    scheduledTime: string | null | undefined;
    endTime: string | null | undefined;
    rescheduleUrl: string | undefined;
    joinUrl: string | undefined;
    lastCallEndedAt: string | undefined;
}

// `location` is free text from Calendly (a street address on some legacy events), so only a Zoom URL becomes a link.
function toZoomJoinUrl(location: string | undefined): string | undefined {
    if (!location) {
        return undefined;
    }
    try {
        const { protocol, hostname } = new URL(location);
        const isZoomHost = hostname === 'zoom.us' || hostname.endsWith('.zoom.us');
        return protocol === 'https:' && isZoomHost ? location : undefined;
    } catch {
        return undefined;
    }
}

// A call stays current until it ends, so a member running late can still find the join link.
function callEndsAt(log: CalendlyLogResponse): number {
    const end = log.end_time ?? log.scheduledtime;
    return end ? new Date(end).getTime() : Number.MAX_SAFE_INTEGER;
}

function callStartsAt(log: CalendlyLogResponse): number {
    return log.scheduledtime ? new Date(log.scheduledtime).getTime() : Number.MAX_SAFE_INTEGER;
}

/**
 * Creates a selector for the next non-canceled call matching any of the slug prefixes that has not ended yet,
 * and for when the most recent one that has ended finished.
 * A call whose time is still pending (webhook not yet received) is kept but ranks after real calls.
 */
export default function createCallSelector(slugs: string[]) {
    // An empty prefix would match every booking the member has.
    const prefixes = slugs.filter(Boolean);
    return (data: CalendlyLogResponse[]): CalendlyCallData => {
        const now = Date.now();
        const calls = data?.filter((log) => prefixes.some((prefix) => log.slug.startsWith(prefix)) && !log.is_canceled);
        const call = calls?.filter((log) => callEndsAt(log) > now).sort((a, b) => callStartsAt(a) - callStartsAt(b))[0];
        const lastEndedCall = calls
            ?.filter((log) => callEndsAt(log) <= now)
            .sort((a, b) => callEndsAt(b) - callEndsAt(a))[0];

        return {
            isScheduled: !!call,
            slug: call?.slug,
            scheduledTime: call?.scheduledtime,
            endTime: call?.end_time,
            rescheduleUrl: call?.reschedule_url,
            joinUrl: toZoomJoinUrl(call?.location),
            lastCallEndedAt: lastEndedCall
                ? lastEndedCall.end_time ?? lastEndedCall.scheduledtime ?? undefined
                : undefined,
        };
    };
}
